import React, { Suspense, lazy, useState, useEffect } from 'react'; // Import lazy and hooks
// Import Outlet for nested routing within protected layouts
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import Login from './pages/Login';
import AdminDashboard from './pages/Admin/Dashboard';
import CommissionList from './pages/Admin/CommissionList';
import CommissionForm from './pages/Admin/CommissionForm';
import GlobalCommissions from './pages/Admin/GlobalCommissions';
import AgentDashboard from './pages/Agent/Dashboard';
import AgentProducts from './pages/Agent/Products'; // Import new AgentProducts component
import AgentSettings from './pages/Agent/Settings'; // Import AgentSettings component
import AgentEarnings from './pages/Agent/Earnings'; // Import the new Agent Earnings component
import AgentLeads from './pages/Agent/Leads'; // Import the Agent Leads component
import CibilDetails from './pages/Agent/CibilDetails'; // Import CibilDetails component
import LeadDetails from './pages/Agent/LeadDetails';
import AgentCreateInvoice from './pages/Agent/CreateInvoice'; // Import Agent CreateInvoice component
import AgentInvoices from './pages/Agent/Invoices'; // Import Agent Invoices component
import AgentInvoiceDetails from './pages/Agent/InvoiceDetails'; // Import Agent InvoiceDetails component
import DSODashboard from './pages/DSO/Dashboard';
import DSOHome from './pages/DSO/Home';
import DSOSettings from './pages/DSO/Settings';
import DSOTeam from './pages/DSO/Team';
import DSOLeads from './pages/DSO/Leads';
import DSOProducts from './pages/DSO/Products';
import DSOCommissions from './pages/DSO/Commissions'; // Import the Commissions component
import DSOEarnings from './pages/DSO/Earnings'; // Import the new component
import DSOBackoffice from './pages/DSO/Backoffice';
import Onboarding from './pages/DSO/Onboarding'; // Import the new Onboarding component
import DSOPayouts from './pages/DSO/Payouts'; // Import the Payouts component
import DSOInvoices from './pages/DSO/Invoices'; // Import the Invoices component
import DSOAgentInvoices from './pages/DSO/AgentInvoices'; // Import the Agent Invoices component
import DSOPayoutDetails from './pages/DSO/PayoutDetails'; // Import the PayoutDetails component
import DSORequestMyPayoutDetails from './pages/DSO/RequestMyPayoutDetails'; // Import the MyPayoutDetails component
import DSOInvoiceDetails from './pages/DSO/InvoiceDetails'; // Import the InvoiceDetails component
import EditLead from './pages/DSO/EditLead'; // Import the EditLead component
import BackofficeDashboard from './pages/Backoffice/Dashboard';
import BackofficeHome from './pages/Backoffice/Home';
import BackofficeLeads from './pages/Backoffice/Leads';
import BackofficeInvoices from './pages/Backoffice/Invoices';
import BackofficeSettings from './pages/Backoffice/Settings';
import AddAgent from './pages/DSO/AddAgent'; // Import AddAgent
// Import RM components
import RMDashboard from './pages/RM/Dashboard';
import RMHome from './pages/RM/Home';
import RMOwners from './pages/RM/Owners';
import OwnerDetails from './pages/RM/OwnerDetails';
import RMPayouts from './pages/RM/Payouts';
import PayoutDetails from './pages/RM/PayoutDetails';
import CreatePayout from './pages/RM/CreatePayout'; // Import the new component
import RMInvoices from './pages/RM/Invoices';
import InvoiceDetails from './pages/RM/InvoiceDetails'; // Import the new component
import RMLeads from './pages/RM/Leads'; // Import the RM Leads component
import RMSettings from './pages/RM/Settings';

// Import WFBackoffice components
import WFBackofficeDashboard from './pages/WFBackoffice/WFBackofficeDashboard';
import WFBackofficeInvoices from './pages/WFBackoffice/WFBackofficeInvoices';
import WFBackofficeDsoPartners from './pages/WFBackoffice/WFBackofficeDsoPartners';
import WFBackofficeAgents from './pages/WFBackoffice/WFBackofficeAgents';

// Import useAuth hook, DSOGuard component, and API services
import { AuthProvider, useAuth } from './hooks/useAuth';
import DSOGuard from './components/DSOGuard'; // Import the DSOGuard component
import WFBackofficeGuard from './components/WFBackofficeGuard'; // Import the WFBackofficeGuard component
import { dsoApi } from './services/api'; // Import the dsoApi for checking profile
import './App.css';

// Modified Protected Route component with Role-Based Access Control
const ProtectedRoute = ({ allowedRoles }) => { // Accept allowedRoles prop
  const token = localStorage.getItem('token');
  const { isAuthenticated } = useAuth(); // Just use the isAuthenticated helper
  
  // Get user directly from localStorage to avoid circular dependencies
  const storedUser = localStorage.getItem('user');
  const currentUser = storedUser ? JSON.parse(storedUser) : null;

  // Add debugging information
  console.log('ProtectedRoute - Auth check:', {
    allowedRoles,
    currentUser: currentUser ? {
      id: currentUser.id,
      user_type: currentUser.user_type,
    } : null,
    tokenExists: !!token
  });

  const isUserAuthenticated = isAuthenticated();
  // Check if authenticated AND user exists AND user role is allowed (case-insensitive)
  const isAuthorized = isUserAuthenticated &&
                       currentUser &&
                       currentUser.user_type && // Add check for user_type existence
                       allowedRoles.map(role => role.toLowerCase()) // Convert allowed roles to lowercase
                                   .includes(currentUser.user_type.toLowerCase()); // Convert user's role to lowercase
  
  console.log('Auth decision:', { isUserAuthenticated, isAuthorized });

  if (!isUserAuthenticated) {
    // Not logged in, redirect to login
    return <Navigate to="/login" replace />;
  }

  if (!isAuthorized) {
    // Logged in, but not the correct role
    console.warn(`Unauthorized access attempt to route requiring roles: ${allowedRoles.join(', ')}. User role: ${currentUser?.user_type}`);
    
    // Redirect to the appropriate dashboard based on user role
    if (currentUser?.user_type) {
      const roleRouteMap = {
        'ADMIN': '/admin',
        'AGENT': '/agent',
        'DSO': '/dso',
        'RM': '/rm',
        'BACKOFFICE': '/backoffice',
        'WFBACKOFFICE': '/wfbackoffice'
      };
      
      const userType = currentUser.user_type.toUpperCase();
      const redirectPath = roleRouteMap[userType] || '/login';
      console.log(`Redirecting to appropriate dashboard: ${redirectPath}`);
      return <Navigate to={redirectPath} replace />;
    }
    
    return <Navigate to="/login" replace />;
  }

  // If authenticated and authorized, render the nested routes via Outlet
  return <Outlet />;
};

// Define lazy loaded components outside the main return for clarity
const AgentDetailLazy = lazy(() => import('./pages/DSO/AgentDetail'));

// Default redirect component that checks auth status
const DefaultRedirect = () => {
  const token = localStorage.getItem('token');
  const { getCurrentUser } = useAuth();
  const currentUser = getCurrentUser();
  const [checkingDsoStatus, setCheckingDsoStatus] = useState(false);
  const [shouldRedirectToOnboarding, setShouldRedirectToOnboarding] = useState(false);

  console.log('DefaultRedirect - User check:', {
    currentUser: currentUser ? {
      id: currentUser.id,
      user_type: currentUser.user_type,
    } : null,
    tokenExists: !!token
  });

  useEffect(() => {
    // Check if the user is a DSO and needs onboarding
    const checkDsoOnboardingStatus = async () => {
      if (token && currentUser?.user_type === 'DSO') {
        setCheckingDsoStatus(true);
        try {
          const response = await dsoApi.getDsoProfile();
          const dsoProfile = response.data.dso_profile;
          
          // Check if DSO has completed onboarding
          const docsUploaded = !!(
            dsoProfile.adhaar_card_front_file && 
            dsoProfile.adhaar_card_back_file && 
            dsoProfile.pan_card_file
          );
          const agreementSigned = !!dsoProfile.agreement_signed;
          const isVerified = !!dsoProfile.is_verified;
          
          // If any condition is not met, redirect to onboarding
          if (!docsUploaded || !agreementSigned || !isVerified) {
            setShouldRedirectToOnboarding(true);
          }
        } catch (error) {
          console.error('Error checking DSO onboarding status:', error);
          // On error, default to onboarding page for safety
          setShouldRedirectToOnboarding(true);
        } finally {
          setCheckingDsoStatus(false);
        }
      }
    };
    
    checkDsoOnboardingStatus();
  }, [token, currentUser]);

  // Show loading state while checking DSO status
  if (checkingDsoStatus) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" />
        <span className="ms-2">Checking account status...</span>
      </div>
    );
  }

  if (token && currentUser?.user_type) {
    // If logged in, redirect based on user role
    const userType = currentUser.user_type.toUpperCase();
    
    switch (userType) {
      case 'DSO':
        // If DSO needs onboarding, redirect to onboarding page
        if (shouldRedirectToOnboarding) {
          return <Navigate to="/dso/onboarding" replace />;
        }
        return <Navigate to="/dso/home" replace />;
      case 'AGENT':
        return <Navigate to="/agent/dashboard" replace />;
      case 'ADMIN':
        return <Navigate to="/admin" replace />;
      case 'RM':
        return <Navigate to="/rm/home" replace />;
      case 'BACKOFFICE':
        return <Navigate to="/backoffice/leads" replace />;
      case 'WFBACKOFFICE':
        return <Navigate to="/wfbackoffice/dashboard" replace />;
      default:
        // If unknown role, redirect to login
        return <Navigate to="/login" replace />;
    }
  }

  // Not logged in, redirect to login
  return <Navigate to="/login" replace />;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />

          {/* Admin Routes - Protected */}
          <Route element={<ProtectedRoute allowedRoles={['ADMIN']} />}>
            {/* All admin routes are managed through AdminDashboard component */}
            <Route path="/admin/*" element={<AdminDashboard />} />
          </Route>

          {/* Agent Routes - Protected */}
          <Route element={<ProtectedRoute allowedRoles={['AGENT']} />}>
            <Route path="/agent" element={<Navigate to="/agent/dashboard" replace />} />
            <Route path="/agent/dashboard" element={<AgentDashboard />} />
            <Route path="/agent/leads" element={<AgentLeads />} />
            <Route path="/agent/products" element={<AgentProducts />} />
            <Route path="/agent/cibil/:cibilId" element={<CibilDetails />} />
            <Route path="/agent/lead/:leadId" element={<LeadDetails />} />
            <Route path="/agent/earnings" element={<AgentEarnings />} />
            <Route path="/agent/settings" element={<AgentSettings />} />
            <Route path="/agent/create-invoice" element={
                <Suspense fallback={<div>Loading...</div>}>
                    <AgentCreateInvoice />
                </Suspense>
            } />
            <Route path="/agent/invoices" element={
                <Suspense fallback={<div>Loading...</div>}>
                    <AgentInvoices />
                </Suspense>
            } />
            <Route path="/agent/invoices/:id" element={
                <Suspense fallback={<div>Loading...</div>}>
                    <AgentInvoiceDetails />
                </Suspense>
            } />
          </Route>

          {/* DSO Routes - Protected */}
          <Route element={<ProtectedRoute allowedRoles={['DSO']} />}>
            {/* Separate route for onboarding without Dashboard layout */}
            <Route path="/dso/onboarding" element={<Onboarding />} />
            
            {/* Parent route using DSODashboard as the layout for all other DSO pages */}
            {/* Wrap in DSOGuard to check onboarding status */}
            <Route path="/dso" element={
              <DSOGuard>
                <DSODashboard />
              </DSOGuard>
            }>
              {/* Child routes rendered within DSODashboard via Outlet */}
              <Route index element={<Navigate to="home" replace />} /> {/* Default route */}
              <Route path="home" element={<DSOHome />} />
              <Route path="team" element={<DSOTeam />} />
              <Route
                path="agents/:id"
                element={
                  <Suspense fallback={<div>Loading Agent Details...</div>}>
                    <AgentDetailLazy />
                  </Suspense>
                }
              />
              <Route path="agents/add" element={<AddAgent />} />
              <Route path="leads" element={<DSOLeads />} />
              <Route path="leads/:leadId/edit" element={<EditLead />} />
              <Route path="backoffice" element={<DSOBackoffice />} />
              <Route path="products" element={<DSOProducts />} /> {/* Add Product Route */}
              <Route path="payouts/commissions" element={<DSOCommissions />} />
              <Route path="payouts" element={<DSOPayouts />} />
              <Route path="payouts/request" element={<DSORequestMyPayoutDetails />} />
              <Route path="payouts/:payoutId/details" element={<DSOPayoutDetails />} />
              <Route path="invoices" element={<DSOInvoices />} />
              <Route path="invoices/:invoiceId/details" element={<DSOInvoiceDetails />} />
              <Route path="agent-invoices" element={<DSOAgentInvoices />} />
              <Route path="agent-invoices/:invoiceId/details" element={<DSOInvoiceDetails />} />
              <Route path="earnings" element={<DSOEarnings />} /> {/* Add Earnings Route */}
              <Route path="settings" element={<DSOSettings />} />
            </Route>
          </Route>

          {/* Backoffice Routes - Protected */}
          <Route element={<ProtectedRoute allowedRoles={['BACKOFFICE']} />}>
            <Route path="/backoffice" element={<BackofficeDashboard />}>
              <Route index element={<Navigate to="leads" replace />} />
              <Route path="leads" element={<BackofficeLeads />} />
              <Route path="invoices" element={<BackofficeInvoices />} />
              <Route path="settings" element={<BackofficeSettings />} />
            </Route>
          </Route>

          {/* RM Routes - Protected */}
          <Route element={<ProtectedRoute allowedRoles={['RM']} />}>
            {/* Parent route using RMDashboard as the layout */}
            <Route path="/rm" element={<RMDashboard />}>
              {/* Child routes rendered within RMDashboard via Outlet */}
              <Route index element={<Navigate to="home" replace />} />
              <Route path="home" element={<RMHome />} />
              <Route path="owners" element={<RMOwners />} />
              <Route path="owners/:id/detail" element={<OwnerDetails />} />
              <Route path="payouts" element={<RMPayouts />} />
              <Route path="payouts/create" element={<CreatePayout />} />
              <Route path="payouts/:payoutId/details" element={<PayoutDetails />} />
              <Route path="invoices" element={<RMInvoices />} />
              {/* Add the new route for invoice details */}
              <Route path="invoices/:invoiceId/details" element={<InvoiceDetails />} />
              <Route path="leads" element={<RMLeads />} />
              <Route path="settings" element={<RMSettings />} />
              {/* Add other nested RM routes here */}
            </Route>
          </Route>
          
          {/* WFBackoffice Routes - Protected */}
          <Route element={<ProtectedRoute allowedRoles={['WFBACKOFFICE']} />}>
            <Route path="/wfbackoffice" element={<Navigate to="/wfbackoffice/dashboard" replace />} />
            <Route path="/wfbackoffice/dashboard" element={<WFBackofficeDashboard />} />
            <Route path="/wfbackoffice/invoices" element={<WFBackofficeInvoices />} />
            <Route path="/wfbackoffice/dso-partners" element={<WFBackofficeDsoPartners />} />
            <Route path="/wfbackoffice/agents" element={<WFBackofficeAgents />} />
          </Route>

          {/* Default route with conditional redirect based on auth status */}
          <Route 
            path="/" 
            element={
              <DefaultRedirect />
            } 
          />

          {/* Optional: Add a 404 Not Found route */}
          {/* <Route path="*" element={<NotFoundComponent />} /> */}
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
